/**
 * Nativer Newsletter-Popup (Ersatz fuer die POWR-App).
 *
 * Der Formularzustand kommt vom Server-Roundtrip und gilt fuer alle
 * customer-Forms einer Seite. Dieses Element ordnet eine Submission sich selbst
 * nur zu, wenn beim Absenden ein sessionStorage-Pending-Flag gesetzt wurde oder
 * die Rueckkehr-URL den Anker #NewsletterPopup traegt. Alles andere gilt als
 * fremde Submission (Homepage-Section) und wird aufgeraeumt statt uebernommen.
 *
 * Ohne HTMLDialogElement (iOS < 15.4) und ohne JS passiert nichts: der Dialog
 * bleibt geschlossen, es gibt keine Regression.
 */
(function () {
  'use strict';

  const KEY_DISMISSED = 'gw-newsletter-popup:dismissed-until';
  const KEY_SUBSCRIBED = 'gw-newsletter-popup:subscribed';
  const KEY_PENDING = 'gw-newsletter-popup:pending';
  const PENDING_TTL = 15 * 60 * 1000;
  const DAY = 864e5;
  const OPEN_CLASS = 'newsletter-popup-open';
  const ANCHOR = '#NewsletterPopup';

  function readStore(store, key) {
    try {
      return window[store].getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeStore(store, key, value) {
    try {
      window[store].setItem(key, value);
    } catch (e) {
      /* Safari Private Mode, Storage voll: Popup laeuft dann ohne Gedaechtnis. */
    }
  }

  function removeStore(store, key) {
    try {
      window[store].removeItem(key);
    } catch (e) {
      /* siehe writeStore */
    }
  }

  class NewsletterPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-popup-dialog]');
      if (!this.dialog || typeof window.HTMLDialogElement === 'undefined') return;
      if (typeof this.dialog.showModal !== 'function') return;

      this.form = this.querySelector('form');
      this.input = this.querySelector('#NewsletterPopup-email');
      this.fieldWrapper = this.querySelector('[data-popup-field]');
      this.errorNode = this.querySelector('[data-popup-error]');
      this.successNode = this.querySelector('[data-popup-success]');
      this.guardNode = this.querySelector('[data-botschutz-meldung]');
      this.decoy = this.querySelector('[name="gw_website"]');
      this.opened = false;
      this.openedAt = 0;
      this.suppressDismissWrite = false;

      this.onCloseButton = () => this.dialog.close();
      this.onDialogClick = (event) => {
        if (event.target === this.dialog) this.dialog.close();
      };
      this.onDialogClose = () => this.handleClose();
      /**
       * Der Riegel laeuft in der Capture-Phase und stoppt die Weitergabe, nicht
       * nur die Standardaktion. Beides ist noetig, im Browser nachgemessen:
       * ein fremder submit-Listener auf demselben Formular erzwingt die
       * Absendung ueber eine vor jedem Patch gesicherte Referenz auf
       * HTMLFormElement.prototype.submit. Der Aufruf taucht damit in keinem
       * Monkey-Patch auf, und ein blosses preventDefault in der Bubble-Phase
       * bleibt wirkungslos: das Formular postet trotz defaultPrevented === true
       * (verifiziert an einem echten Kunden, dessen updatedAt sprang).
       *
       * Wer das anzweifelt, prueft es so: Listener in der Capture-Phase mit
       * preventDefault UND stopImmediatePropagation haelt, derselbe Listener in
       * der Bubble-Phase haelt nicht. Genau diese Asymmetrie ist der Beleg.
       *
       * stopImmediatePropagation trifft nur den Sperrfall. Geht die Anmeldung
       * durch, laeuft das Event unangetastet weiter an alle anderen Listener.
       */
      this.onSubmit = (event) => {
        // Alte Meldung erst weg, sonst bleibt sie nach einem geglueckten
        // zweiten Versuch neben der Erfolgsmeldung stehen.
        if (this.guardNode) this.guardNode.hidden = true;
        if (this.botVerdacht()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showGuardMessage();
          return;
        }
        writeStore('sessionStorage', KEY_PENDING, String(Date.now()));
      };
      this.onScroll = () => this.checkScroll();

      const closeButton = this.querySelector('[data-popup-close]');
      if (closeButton) closeButton.addEventListener('click', this.onCloseButton);
      this.dialog.addEventListener('click', this.onDialogClick);
      this.dialog.addEventListener('close', this.onDialogClose);
      if (this.form) this.form.addEventListener('submit', this.onSubmit, { capture: true });

      if (window.Shopify && window.Shopify.designMode) {
        this.setupDesignMode();
        return;
      }

      this.resolveState();
    }

    disconnectedCallback() {
      this.clearTriggers();
      this.unlockScroll();
      if (this.onSectionSelect) {
        document.removeEventListener('shopify:section:select', this.onSectionSelect);
        document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
      }
    }

    /* --- Theme-Editor: kein Auto-Trigger, dafuer Vorschau bei Section-Auswahl --- */
    setupDesignMode() {
      this.onSectionSelect = (event) => {
        if (event.target.contains(this)) this.open();
      };
      this.onSectionDeselect = (event) => {
        if (event.target.contains(this) && this.dialog.open) {
          this.suppressDismissWrite = true;
          this.dialog.close();
        }
      };
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
    }

    /* --- Zustand nach dem Laden bestimmen (Reihenfolge ist die Logik) --- */
    resolveState() {
      const pending = this.consumePending();
      const posted = new URLSearchParams(window.location.search).get('customer_posted') === 'true';
      const anchored = window.location.hash === ANCHOR;

      // 1. Eigener Fehlversuch: sofort zeigen, Wert bleibt stehen, kein Dismiss.
      if (pending && this.errorNode) {
        this.suppressDismissWrite = true;
        this.open();
        return;
      }

      // 2. Eigene erfolgreiche Anmeldung: Erfolgsmeldung enthuellen.
      if ((pending || anchored) && posted) {
        writeStore('localStorage', KEY_SUBSCRIBED, '1');
        this.showSuccess();
        return;
      }

      // 3. Fremde erfolgreiche Anmeldung (Homepage-Section, Footer): jede
      //    customer_posted-Submission in diesem Theme ist ein Newsletter-Signup.
      if (posted) {
        writeStore('localStorage', KEY_SUBSCRIBED, '1');
        return;
      }

      // 4. Fremder Fehler ist ins Popup-Markup geleakt: aufraeumen.
      if (this.errorNode) this.clearForeignError();

      // 5. Unterdrueckung: schon abonniert oder bewusst geschlossen.
      if (readStore('localStorage', KEY_SUBSCRIBED) === '1') return;
      const dismissedUntil = parseInt(readStore('localStorage', KEY_DISMISSED), 10);
      if (dismissedUntil && dismissedUntil > Date.now()) return;

      this.setupTriggers();
    }

    consumePending() {
      const raw = readStore('sessionStorage', KEY_PENDING);
      removeStore('sessionStorage', KEY_PENDING);
      const ts = parseInt(raw, 10);
      if (!ts) return false;
      return Date.now() - ts < PENDING_TTL;
    }

    clearForeignError() {
      this.errorNode.remove();
      this.errorNode = null;
      if (this.input) {
        this.input.removeAttribute('aria-invalid');
        this.input.removeAttribute('aria-describedby');
        this.input.value = '';
      }
    }

    showSuccess() {
      this.suppressDismissWrite = true;
      if (this.errorNode) this.clearForeignError();
      if (this.fieldWrapper) this.fieldWrapper.setAttribute('hidden', '');
      if (this.successNode) this.successNode.removeAttribute('hidden');
      this.open(this.querySelector('#NewsletterPopup-success'));
    }

    /* --- Auslöser --- */
    setupTriggers() {
      // Beide Werte auf 0 heisst: Popup ist im Theme-Editor bewusst abgeschaltet.
      const delay = parseInt(this.dataset.delaySeconds, 10);
      if (delay > 0) this.timer = setTimeout(() => this.open(), delay * 1000);
      if (this.scrollPercent() > 0) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }
    }

    scrollPercent() {
      const value = parseInt(this.dataset.scrollPercent, 10);
      return isNaN(value) ? 0 : value;
    }

    checkScroll() {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const reached = (window.scrollY / scrollable) * 100;
      if (reached >= this.scrollPercent()) this.open();
    }

    clearTriggers() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
      window.removeEventListener('scroll', this.onScroll);
    }

    /**
     * Bot-Schutz, zwei unabhaengige Signale.
     *
     * 1. Der Koeder gw_website ist ausgefuellt. Menschen sehen das Feld nicht,
     *    erreichen es nicht per Tab und Screenreader ueberspringen es. Wer dort
     *    etwas eintraegt, hat das Formular maschinell befuellt.
     * 2. Zwischen Oeffnen und Absenden liegt weniger als die Mindestzeit. Ein
     *    Mensch braucht das Popup erst zu lesen und die Adresse zu tippen.
     *
     * Reichweite dieser Pruefung, damit niemand mehr davon erwartet: sie greift
     * nur bei Automaten, die die Seite wirklich rendern und das Formular im DOM
     * abschicken. Ein Skript, das direkt auf Shopifys /contact postet, sieht
     * weder Koeder noch Uhr. Dagegen hilft kein Theme-Code, sondern nur ein
     * Schutz vor dem Endpunkt.
     *
     * Ohne JS laeuft der Popup ohnehin nicht (kein Dialog), es entsteht also
     * kein Weg, der die Pruefung umgeht und trotzdem das Popup benutzt.
     */
    botVerdacht() {
      if (this.decoy && this.decoy.value.trim() !== '') return true;

      const minSeconds = parseInt(this.dataset.minSeconds, 10);
      if (!(minSeconds > 0)) return false;
      // Ohne bekannten Oeffnungszeitpunkt nicht blockieren: lieber eine
      // Anmeldung zu viel als eine echte abgewiesen.
      if (!this.openedAt) return false;
      return Date.now() - this.openedAt < minSeconds * 1000;
    }

    showGuardMessage() {
      if (!this.guardNode) return;
      this.guardNode.hidden = false;
      if (this.input) {
        this.input.setAttribute('aria-describedby', 'NewsletterPopup-botschutz');
        this.input.focus();
      }
    }

    open(focusTarget) {
      if (this.opened) return;
      this.opened = true;
      this.openedAt = Date.now();
      this.clearTriggers();
      this.releaseForeignAutofocus();
      this.dialog.showModal();
      this.lockScroll();
      this.focusInDialog(focusTarget || this.input);
    }

    /**
     * Scroll-Lock ohne Layoutsprung. Die Klasse nimmt html und body das
     * overflow, sonst uebernimmt body das Scrollen des Viewports. Damit
     * verschwindet aber die Scrollbar, die Seite wird um deren Breite breiter
     * und das kostet CLS (gemessen 0,11). Die Breite wird deshalb als
     * padding-right zurueckgegeben, bevor die Klasse greift.
     */
    lockScroll() {
      const gap = window.innerWidth - document.documentElement.clientWidth;
      if (gap > 0) {
        this.scrollbarGap = gap;
        document.body.style.paddingRight = gap + 'px';
      }
      document.documentElement.classList.add(OPEN_CLASS);
    }

    unlockScroll() {
      document.documentElement.classList.remove(OPEN_CLASS);
      if (this.scrollbarGap) {
        document.body.style.paddingRight = '';
        this.scrollbarGap = 0;
      }
    }

    /**
     * Dawn rendert die Erfolgsmeldung der Homepage-Section mit autofocus. Nach
     * einer eigenen Submission steht diese Meldung mit auf der Seite, liegt aber
     * ausserhalb des Modals und ist damit inert: der Autofocus-Flush kann sie
     * nicht fokussieren und setzt den Fokus stattdessen auf <body> zurueck,
     * womit unser Ziel ihn wieder verliert (im Browser nachgemessen: focus auf
     * das Ziel bei 343 ms, Blur durch den Flush bei 345 ms). Das Attribut kommt
     * deshalb weg, bevor der Flush laeuft. Verloren geht dabei nichts, ein
     * inertes Element kann den Fokus ohnehin nicht annehmen.
     */
    releaseForeignAutofocus() {
      const foreign = document.querySelectorAll('[autofocus]');
      for (let i = 0; i < foreign.length; i++) {
        if (!this.dialog.contains(foreign[i])) foreign[i].removeAttribute('autofocus');
      }
    }

    /**
     * Setzt den Fokus und haelt ihn im Modal. Natives dialog fangt nur die
     * Fokusbewegung des Nutzers ab, keine programmatischen Ruecksetzer: Chrome
     * leert den Fokus zum Beispiel beim Sprung auf einen Anker, dessen Ziel
     * nicht fokussierbar ist. Genau das passiert auf der Rueckkehr-URL
     * ?customer_posted=true#NewsletterPopup, denn der Anker ist das <form>
     * (nachgemessen: Fokus landete danach auf <body>).
     */
    focusInDialog(target) {
      if (!target) return;
      this.focusTarget = target;
      target.focus();
      if (this.onFocusOut) return;
      this.onFocusOut = (event) => {
        if (!this.dialog.open) return;
        if (event.relatedTarget && this.dialog.contains(event.relatedTarget)) return;
        requestAnimationFrame(() => {
          if (this.dialog.open && this.focusTarget && !this.dialog.contains(document.activeElement)) {
            this.focusTarget.focus();
          }
        });
      };
      this.dialog.addEventListener('focusout', this.onFocusOut);
    }

    handleClose() {
      this.unlockScroll();
      // Im Theme-Editor nie ins localStorage schreiben, sonst blendet sich der
      // Popup im echten Browser des Merchants fuer 30 Tage aus.
      if (window.Shopify && window.Shopify.designMode) {
        this.opened = false;
        this.suppressDismissWrite = false;
        return;
      }
      if (this.suppressDismissWrite) {
        this.suppressDismissWrite = false;
        return;
      }
      const days = parseInt(this.dataset.dismissDays, 10) || 30;
      writeStore('localStorage', KEY_DISMISSED, String(Date.now() + days * DAY));
    }
  }

  if (!window.customElements.get('newsletter-popup')) {
    window.customElements.define('newsletter-popup', NewsletterPopup);
  }
})();
